"use client";

import { useEffect, useCallback, useState } from "react";

export interface ShortcutDef {
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  handler: () => void;
  description: string;
  category: string;
}

interface KeyboardShortcutsOptions {
  shortcuts?: ShortcutDef[];
  enabled?: boolean;
}

export function useKeyboardShortcuts(options: KeyboardShortcutsOptions = {}) {
  const [helpOpen, setHelpOpen] = useState(false);

  const helpShortcut: ShortcutDef = {
    key: "?",
    handler: () => setHelpOpen((prev) => !prev),
    description: "Toggle keyboard shortcuts help",
    category: "Navigation",
  };

  const allShortcuts: ShortcutDef[] = [helpShortcut, ...(options.shortcuts ?? [])];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (options.enabled === false) return;

      const target = e.target instanceof HTMLElement ? e.target : null;
      const isInput =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      for (const shortcut of allShortcuts) {
        if (shortcut.key === "Escape" && e.key === "Escape") {
          if (isInput) {
            target?.blur();
            return;
          }
          shortcut.handler();
          return;
        }

        if (shortcut.key === "?" && e.key === "?" && !e.metaKey && !e.ctrlKey && !isInput) {
          e.preventDefault();
          shortcut.handler();
          return;
        }

        const metaMatch = shortcut.meta ? e.metaKey : !e.metaKey;
        const ctrlMatch = shortcut.ctrl ? e.ctrlKey : !e.ctrlKey;
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
        const altMatch = shortcut.alt ? e.altKey : !e.altKey;

        if (e.key.toLowerCase() === shortcut.key.toLowerCase() && metaMatch && ctrlMatch && shiftMatch && altMatch) {
          if (shortcut.meta || shortcut.ctrl) {
            if (isInput) continue;
            e.preventDefault();
          }
          shortcut.handler();
          return;
        }
      }
    },
    [allShortcuts, options.enabled],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return { helpOpen, setHelpOpen, shortcuts: allShortcuts };
}
